/**
 * Normalizer Module
 *
 * Transforms raw API records into normalized domain records with decoded fields,
 * parsed food items, and extracted date/time components.
 *
 * Key behavior:
 * - Uses `recorded_at` (NOT `created_at`) for dateKey extraction
 * - Decodes integer codes to human-readable labels via codeDecoder
 * - Parses food item strings via foodParser
 * - Computes amountScore with inversion (1→3, 2→2, 3→1)
 */

import moment from 'moment';

import type {
  RawInputRecord,
  RawOutputRecord,
  RawNoteRecord,
  NormalizedInputRecord,
  NormalizedOutputRecord,
  NormalizedNoteRecord,
} from '../types';

import {
  decodeFeedingType,
  decodeMealCategory,
  decodeOutputCategory,
  decodeStoolType,
  decodeUrineType,
  decodeAmount,
  decodeBristolScale,
  decodeStoolColor,
  decodeUrineColor,
} from './codeDecoder';

import { parseFoodItems } from './foodParser';

/**
 * Normalize raw input (feeding) records into structured domain records.
 *
 * For each raw record:
 * - dateKey = date portion of recorded_at (YYYY-MM-DD)
 * - timeLabel = time portion of recorded_at formatted as "h:mm A"
 * - mealLabel = decoded meal category
 * - feedingTypeLabel = decoded feeding type
 * - foodItems = parsed food item string
 * - patientId = customer_id
 */
export function normalizeInputRecords(records: RawInputRecord[]): NormalizedInputRecord[] {
  return records.map((record) => {
    const recordedMoment = moment(record.recorded_at);

    return {
      id: record.id,
      patientId: record.customer_id || '',
      recordedAt: record.recorded_at || '',
      dateKey: recordedMoment.format('YYYY-MM-DD'),
      timeLabel: recordedMoment.format('h:mm A'),
      mealCode: record.meal,
      mealLabel: decodeMealCategory(record.meal),
      feedingTypeCode: record.type,
      feedingTypeLabel: decodeFeedingType(record.type),
      foodItems: parseFoodItems(record.food_items || ''),
      notes: record.notes || '',
      creatorName: record.creator_name || '',
      createdAt: record.created_at || '',
    };
  });
}

/**
 * Normalize raw output (stool/urine) records into structured domain records.
 *
 * For each raw record:
 * - dateKey = date portion of recorded_at (YYYY-MM-DD)
 * - timeLabel = time portion of recorded_at formatted as "h:mm A"
 * - outputLabel = decoded output category (Stool/Urine)
 * - typeLabel = decoded stool type or urine type based on category
 * - amountLabel and amountScore from decodeAmount (with inversion)
 * - colorLabel and colorHex from stool or urine color decoder based on category
 * - patternLabel from decodeBristolScale (meaningful for stool, decoded anyway)
 * - patientId = customer_id
 */
export function normalizeOutputRecords(records: RawOutputRecord[]): NormalizedOutputRecord[] {
  return records.map((record) => {
    const recordedMoment = moment(record.recorded_at);

    // Decode type based on category: 1=Stool, 2=Urine
    const typeLabel =
      record.category === 1
        ? decodeStoolType(record.type)
        : decodeUrineType(record.type);

    // Decode amount with inversion
    const { label: amountLabel, score: amountScore } = decodeAmount(record.amount);

    // Decode color based on category
    const { label: colorLabel, hex: colorHex } =
      record.category === 1
        ? decodeStoolColor(record.color)
        : decodeUrineColor(record.color);

    // Decode Bristol scale pattern
    const { label: patternLabel } = decodeBristolScale(record.pattern);

    return {
      id: record.id,
      patientId: record.customer_id || '',
      recordedAt: record.recorded_at || '',
      dateKey: recordedMoment.format('YYYY-MM-DD'),
      timeLabel: recordedMoment.format('h:mm A'),
      outputCode: record.category,
      outputLabel: decodeOutputCategory(record.category),
      typeCode: record.type,
      typeLabel,
      amountCode: record.amount,
      amountLabel,
      amountScore,
      colorCode: record.color,
      colorLabel,
      colorHex,
      patternCode: record.pattern,
      patternLabel,
      notes: record.notes || '',
      creatorName: record.creator_name || '',
      createdAt: record.created_at || '',
    };
  });
}

/**
 * Normalize raw note records into structured domain records.
 *
 * For each raw record:
 * - dateKey = date portion of recorded_at (YYYY-MM-DD)
 * - timeLabel = time portion of recorded_at formatted as "h:mm A"
 * - noteText = record.note
 * - imageUrl = record.image_url
 * - imageId = record.image_id
 * - noteType = the passed parameter (allows caller to specify "standalone", "input", or "output")
 * - patientId = customer_id
 */
export function normalizeNoteRecords(
  records: RawNoteRecord[],
  noteType: string = 'standalone'
): NormalizedNoteRecord[] {
  return records.map((record) => {
    const recordedMoment = moment(record.recorded_at);

    return {
      id: record.id,
      patientId: record.customer_id || '',
      recordedAt: record.recorded_at || '',
      dateKey: recordedMoment.format('YYYY-MM-DD'),
      timeLabel: recordedMoment.format('h:mm A'),
      noteType,
      noteText: record.note || '',
      imageUrl: record.image_url ?? null,
      imageId: record.image_id ?? null,
      creatorName: record.creator_name || '',
      createdAt: record.created_at || '',
    };
  });
}
